import { useState, useCallback } from 'react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export const useChatStream = (agentId, apiKey, chatId, collectedFormData, onChatStateChange) => {
  const [isTyping, setIsTyping] = useState(false);

  const sendMessage = useCallback(async (messageText, setMessages) => {
    if (!messageText.trim() || isTyping) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      text: messageText,
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setIsTyping(true);

    const botMessageId = Date.now() + 1;
    let accumulatedText = '';
    let messageCounter = 0;

    try {
      const chatData = {
        agent_id: agentId,
        prompt: messageText,
        channel: 'platform',
        chat_id: chatId,
        chat_metadata: {
          custom_field: {
            ...collectedFormData,
            channel: 'widget',
            platform_type: 'iframe',
          }
        }
      };
      
      let headers = {
        'Content-Type': 'application/json',
      }

      if (apiKey.startsWith("pawsf")) {
        headers['X-API-KEY'] = apiKey
      } else {
        headers['Authorization'] = `Bearer ${apiKey}`
      }

      const response = await fetch(`${API_BASE_URL}/chat`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(chatData)
      });

      if (!response.ok) {
        throw new Error(`Erro na API: ${response.status} ${response.statusText}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          // Stream finalizado - envia como uma única mensagem
          if (accumulatedText.trim().length > 0) {
            const botMessage = {
              id: `${botMessageId}_${messageCounter}`,
              type: 'assistant',
              text: accumulatedText.trim(),
              timestamp: new Date()
            };
            setMessages(prev => [...prev, botMessage]);
          }
          
          setIsTyping(false);

          try {
            let headers = {
              'Content-Type': 'application/json',
            }

            if (apiKey.startsWith("pawsf")) {
              headers['X-API-KEY'] = apiKey
            } else {
              headers['Authorization'] = `Bearer ${apiKey}`
            }

            const chatStateResponse = await fetch(`${API_BASE_URL}/chats/${chatId}`, {
              headers: headers
            });

            if (chatStateResponse.ok) {
              const chatData = await chatStateResponse.json();
              const newState = chatData.state;
              
              // Notifica o componente pai sobre a mudança de estado via callback
              if (onChatStateChange && newState) {
                onChatStateChange(newState);
              }
            }
          } catch (error) {
            console.error('Erro ao buscar estado do chat:', error);
          }
          break;
        }

        const chunk = decoder.decode(value, { stream: true });

        // Ignorar flag de final state
        if (chunk.startsWith("__FINAL_STATE__")) {
          continue;
        }

        // Acumular texto
        accumulatedText += chunk;
        
        // Detectar quebras explícitas de linha do backend
        if (chunk.includes('\n\n')) {
          // Dupla quebra = separador forte de mensagens
          const parts = accumulatedText.split('\n\n');
          
          // Processa todas as partes completas (exceto a última que pode estar incompleta)
          for (let i = 0; i < parts.length - 1; i++) {
            const text = parts[i].trim();
            
            if (text.length > 0) {
              const botMessage = {
                id: `${botMessageId}_${messageCounter++}`,
                type: 'assistant',
                text: text,
                timestamp: new Date()
              };
              setMessages(prev => [...prev, botMessage]);
            }
          }
          
          // Mantém a última parte no acumulador
          accumulatedText = parts[parts.length - 1];
        }
      }

    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      
      const errorMessage = {
        id: botMessageId,
        type: 'system',
        text: 'Ocorreu um erro ao enviar sua mensagem. Por favor, verifique sua conexão e tente novamente.',
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, errorMessage]);
      setIsTyping(false);
      
      if (error.message.includes('API')) {
        console.error('Erro de API:', error.message);
      } else if (error.name === 'TypeError' && error.message.includes('fetch')) {
        console.error('Erro de rede - não foi possível conectar ao servidor');
      } else {
        console.error('Erro desconhecido:', error);
      }
    }
  }, [agentId, apiKey, chatId, collectedFormData, isTyping]);

  return {
    isTyping,
    sendMessage
  };
};